/**
 * AppRoutes — All route definitions for EventFlow
 * Extracted from App.js for maintainability.
 * Organized by: Public, Module Landings, Member, Churches, Campaigns,
 * Bible, School, Admin, Scanning/Kiosk, Staff, Security, Delegate.
 */
import React from 'react';
import { Routes, Route, Navigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';
import { getToken } from '@/lib/authToken';
import homeFor from '@/lib/homeFor';

// Pages (barrel export)
import {
  LandingPage,
  LoginPage,
  PaymentSuccessPage,
  PaymentCancelPage,
  EventLanding,
  AdminDashboard,
  AdminEventPage,
  DelegatePortal,
  RequireAuth,
  DashboardLayout
} from '@/pages';

// Standalone pages
import CheckinStation from '@/pages/CheckinStation';
import DelegateAccessibility from '@/pages/DelegateAccessibility';
import AccessibilityDisplay from '@/pages/AccessibilityDisplay';
import AccessibilitySource from '@/pages/AccessibilitySource';
import CaptionsLibrary from '@/pages/CaptionsLibrary';
import PrivacyPolicy from '@/pages/legal/PrivacyPolicy';
import CookiePolicy from '@/pages/legal/CookiePolicy';
import InvitationAccept from '@/pages/InvitationAccept';
import MobileScanner from '@/pages/MobileScanner';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import MemberLoginPage from '@/pages/MemberLoginPage';
import TakeQuizPage from '@/pages/TakeQuizPage';
import TakeExamPage from '@/pages/TakeExamPage';
import MemberRegisterPage from '@/pages/MemberRegisterPage';
import ChurchLandingPage from '@/pages/ChurchLandingPage';
import ChurchGivingPage from '@/pages/ChurchGivingPage';
import BishopManjoroPage from '@/pages/BishopManjoroPage';
import MemberPortal from '@/pages/MemberPortal';
import AlumniPortalPage from '@/pages/AlumniPortalPage';
import WalletCardPage from '@/pages/WalletCardPage';
import ParentScanPage from '@/pages/ParentScanPage';
import AITutorPage from '@/pages/AITutorPage';
import CopilotPage from '@/pages/CopilotPage';
import AdaptiveHomeDashboard from '@/pages/AdaptiveHomeDashboard';
import WorkspacePage from '@/pages/WorkspacePage';
import PublicOrgPage from '@/pages/PublicOrgPage';
import ChurchesDirectory from '@/pages/ChurchesDirectory';
import CMSPageView from '@/pages/CMSPageView';
import OrganizationLandingPage from '@/pages/OrganizationLandingPage';
import SchoolSlugRedirect from '@/pages/SchoolSlugRedirect';
import SchoolAdmissionsApply from '@/pages/SchoolAdmissionsApply';
import QrLoginPage from '@/pages/QrLoginPage';
import LiveClassroomList from '@/pages/LiveClassroomList';
import LiveClassroomRoom from '@/pages/LiveClassroomRoom';
import AdminQrBadges from '@/pages/AdminQrBadges';
import AdminCRM from '@/pages/AdminCRM';
import AdminPeopleDirectory from '@/pages/AdminPeopleDirectory';
import AdminAccommodationOps from '@/pages/AdminAccommodationOps';
import AccommodationApply from '@/pages/AccommodationApply';
import AdminCRMContactDetail from '@/pages/AdminCRMContactDetail';
import AdminBackupCentre from '@/pages/AdminBackupCentre';
import AdminDocumentAudit from '@/pages/AdminDocumentAudit';
import AdminPersons from '@/pages/AdminPersons';
import AdminPersonProfile from '@/pages/AdminPersonProfile';
import AdminChurchMember360 from '@/pages/AdminChurchMember360';
import AdminChurchHomeGroupSplit from '@/pages/AdminChurchHomeGroupSplit';
import AdminLeadership from '@/pages/AdminLeadership';
import AdminChurchOpsHub from '@/pages/AdminChurchOpsHub';
import AdminTeamPermissions from '@/pages/AdminTeamPermissions';
import AdminDeniedAttempts from '@/pages/AdminDeniedAttempts';
import AdminDirectorySharing from '../pages/AdminDirectorySharing';
import AdminHierarchyRoles from '../pages/AdminHierarchyRoles';
import AdminDuplicateRolls from '../pages/AdminDuplicateRolls';
import AdminWorshipHub from '@/pages/AdminWorshipHub';
import PresenterConsole from '@/pages/worship/presenter/PresenterConsole';
import PresenterOutput from '@/pages/worship/presenter/PresenterOutput';
import PublicSongPortal from '@/pages/worship/PublicSongPortal';
import PublicLeadershipPage from '@/pages/PublicLeadershipPage';
import ApplyPage from '@/pages/ApplyPage';
import ReferencePage from '@/pages/ReferencePage';
import ChurchJoinPage from '@/pages/ChurchJoinPage';
import AdminControlCentre from '@/pages/AdminControlCentre';
import AdminSystemEvents from '@/pages/AdminSystemEvents';
import AdminNotificationPreferences from '@/pages/AdminNotificationPreferences';
import AdminExecutive from '@/pages/AdminExecutive';
import AdminDuplicationAudit from '@/pages/AdminDuplicationAudit';
import AdminSecurityConsole from '@/pages/AdminSecurityConsole';
import SecurityOps from '@/pages/SecurityOps';
import AdminChurchAudit from '@/pages/AdminChurchAudit';
import AdminBadgeDesigner from '@/pages/AdminBadgeDesigner';
import EventCheckInUniversal from '@/pages/EventCheckInUniversal';
import BookshopTeamPage from '@/pages/BookshopTeamPage';
import BookshopAdmin from '@/pages/BookshopAdmin';
import FwtvTeamPage from '@/pages/FwtvTeamPage';
import AdminMediaIntel from '@/pages/AdminMediaIntel';
import AdminHousingV2 from '@/pages/AdminHousingV2';
import AdminUniversalScanner from '@/pages/AdminUniversalScanner';
import AdminQrLabels from '@/pages/AdminQrLabels';
import AdminPosTabs from '@/pages/AdminPosTabs';
import AdminPosRegisters from '@/pages/AdminPosRegisters';
import AdminWiFi from '@/pages/AdminWiFi';
import AdminLabPCs from '@/pages/AdminLabPCs';
import WiFiCaptivePortal from '@/pages/WiFiCaptivePortal';
import PosCustomerDisplay from '@/pages/PosCustomerDisplay';
import AdminObsBroadcasts from '@/pages/AdminObsBroadcasts';
import AdminVideoEditor from '@/pages/AdminVideoEditor';
import DelegatePreview from '@/pages/DelegatePreview';
import MealScanStation from '@/pages/MealScanStation';
import VerifyCertificatePage from '@/pages/VerifyCertificatePage';
import PrintCertificatePage from '@/pages/PrintCertificatePage';
import AccommodationScanStation from '@/pages/AccommodationScanStation';
import UnifiedKiosk from '@/pages/UnifiedKiosk';
import ArrivalKiosk from '@/pages/ArrivalKiosk';
import UnifiedLoginPage from '@/pages/UnifiedLoginPage';
import PortalSettings from '@/pages/PortalSettings';
import AdminFinanceDashboard from '@/pages/AdminFinanceDashboard';
import AdminStoragePage from '@/pages/AdminStoragePage';
import AdminFinancialAccess from '@/pages/AdminFinancialAccess';
import AdminUnifiedLedger from '@/pages/AdminUnifiedLedger';
import AdminUnifiedHousing from '@/pages/AdminUnifiedHousing';
import AdminEcosystemHR from '@/pages/AdminEcosystemHR';
import AdminPermissionMatrix from '@/pages/AdminPermissionMatrix';
import AdminScopeRegistrations from '@/pages/AdminScopeRegistrations';
import AdminRbacAudit from '@/pages/AdminRbacAudit';
import AdminRoleBuilder from '@/pages/AdminRoleBuilder';
import AdminCrossSchoolAnalytics from '@/pages/AdminCrossSchoolAnalytics';
import AdminPricingPlans from '@/pages/AdminPricingPlans';
import AdminBiblePlans from '@/pages/AdminBiblePlans';
import PublicScopeRegister from '@/pages/PublicScopeRegister';
import UniversalScanner from '@/pages/UniversalScanner';
import AdminDataCleanup from '@/pages/AdminDataCleanup';
import BankDashboard from '@/pages/BankDashboard';
import MyWallet from '@/pages/MyWallet';
import IncidentsDashboard from '@/pages/IncidentsDashboard';
import GatePassPage from '@/pages/GatePassPage';
import DonorTimeline from '@/pages/DonorTimeline';
import MyEmployments from '@/pages/MyEmployments';
import DonationPortalPage from '@/pages/DonationPortalPage';
import ManjaroBooksPage from '@/pages/ManjaroBooksPage';
import ShopPage, { ShopProductPage, ShopCheckoutPage, ShopOrderPage } from '@/pages/ShopPage';
import AdminShop from '@/pages/AdminShop';
import FaithWorksTVAdmin from '@/pages/FaithWorksTVAdmin';
import SchoolManagement from '@/pages/SchoolManagement';
import SchoolDetailPage from '@/pages/SchoolDetailPage';
import SchoolLoginPage from '@/pages/SchoolLoginPage';
import SchoolResetPasswordPage from '@/pages/SchoolResetPasswordPage';
import SchoolChangePasswordPage from '@/pages/SchoolChangePasswordPage';
import TeacherPortal from '@/pages/TeacherPortal';
import StudentPortal from '@/pages/StudentPortal';
import ParentPortal from '@/pages/ParentPortal';
import SchoolWebsitePage from '@/pages/SchoolWebsitePage';
import SchoolApplyPage from '@/pages/SchoolApplyPage';
import PlatformApplyPage from '@/pages/PlatformApplyPage';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import TranscriptionJobsAdmin from '@/pages/TranscriptionJobsAdmin';
import PricingPage from '@/pages/PricingPage';
import AdminHome from '@/pages/AdminHome';
import { ImpersonationBanner } from '@/components/superadmin/ImpersonationBanner';
import { AdminImpersonationBanner } from '@/components/superadmin/AdminImpersonationBanner';
import SecurityLoginPage from '@/pages/SecurityLoginPage';
import DiningStaffLogin from '@/pages/DiningStaffLogin';
import HousingStaffLogin from '@/pages/HousingStaffLogin';
import AdminDonationsPage from '@/pages/AdminDonationsPage';
import GivingDashboard from '@/pages/GivingDashboard';
import ApprovalInbox from '@/pages/ApprovalInbox';
import RoleChangeLog from '@/pages/RoleChangeLog';
import AdminScopesPage from '@/pages/AdminScopesPage';
import PublicChurchPage from '@/pages/PublicChurchPage';

// Module Landing Pages
import BibleLandingPage from '@/pages/BibleLandingPage';
import SchoolsDirectoryPage from '@/pages/SchoolsDirectoryPage';
import SermonsPublicPage from '@/pages/SermonsPublicPage';
import FaithWorksTVPage from '@/pages/FaithWorksTVPage';
import FwtvPremiumAdmin from '@/pages/FwtvPremiumAdmin';
import FwtvStudioAdmin from '@/pages/FwtvStudioAdmin';
import SermonPage from '@/pages/SermonPage';
import GivingLandingPage from '@/pages/GivingLandingPage';
import EventsLandingPage from '@/pages/EventsLandingPage';
import ChurchesLandingPage from '@/pages/ChurchesLandingPage';

// Bible
import BiblePage from '@/pages/BiblePage';
import BibleReadingPlanPage from '@/pages/BibleReadingPlanPage';
import BibleHome from '@/pages/BibleHome';
import AdminBibleDashboard from '@/pages/AdminBibleDashboard';

// Campaign pages
import CampaignLanding from '@/pages/CampaignLanding';
import CampaignDonate from '@/pages/CampaignDonate';
import OrgSitePage from '@/pages/OrgSitePage';
import AdminChurchSitePage from '@/pages/AdminChurchSitePage';
import AdminABMHCFeaturedMedia from '@/pages/AdminABMHCFeaturedMedia';
import AdminABMHCOneComCMS from '@/pages/AdminABMHCOneComCMS';
import AdminFHBFOneComCMS from '@/pages/AdminFHBFOneComCMS';
import AdminTenantDomains from '@/pages/AdminTenantDomains';
import AdminLandingEditor from '@/pages/AdminLandingEditor';
import AdminHeroImages from '@/pages/AdminHeroImages';
import AdminEmbedsHealth from '@/pages/AdminEmbedsHealth';
import AdminTributesPage from '@/pages/AdminTributesPage';
import MotherGeneralTributesAdmin from '@/pages/MotherGeneralTributesAdmin';
import PresentationStudio from '@/pages/PresentationStudio';
import ProjectWorkspace from '@/pages/ProjectWorkspace';
import AdminMediaLibraryPage from '@/pages/AdminMediaLibraryPage';
import BroadcastingGuidePage from '@/pages/BroadcastingGuidePage';
import AdminPrayerRequestsPage from '@/pages/AdminPrayerRequestsPage';
import AdminAppointmentsPage from '@/pages/AdminAppointmentsPage';
import AdminPatientStoriesPage from '@/pages/AdminPatientStoriesPage';
import GivePage from '@/pages/GivePage';
import AdminDonationPagesPage from '@/pages/AdminDonationPagesPage';
import AdminSchoolWiringPage from '@/pages/AdminSchoolWiringPage';
import CampaignTrackerEmbed from '@/pages/CampaignTrackerEmbed';
import CampaignMilestones from '@/pages/CampaignMilestones';
import CampaignGallery from '@/pages/CampaignGallery';
import CampaignUpdates from '@/pages/CampaignUpdates';
import CampaignContact from '@/pages/CampaignContact';
import CampaignsList from '@/pages/CampaignsList';
import MyCampaigns from '@/pages/MyCampaigns';
import StudioPortal from '@/pages/StudioPortal';
import StudioLogin from '@/pages/StudioLogin';
import StudioBookPublic from '@/pages/StudioBookPublic';
import OpsPortal from '@/pages/OpsPortal';
import HousingBookPublic from '@/pages/HousingBookPublic';
import OpsHub from '@/pages/OpsHub';
import ChurchPublicPage from '@/pages/ChurchPublicPage';
import ChurchWelcomePage from '@/pages/ChurchWelcomePage';
import FindChurchPage from '@/pages/FindChurchPage';
import ChurchLivePage from '@/pages/ChurchLivePage';
import TeamLoginPortal from '@/components/TeamLoginPortal';
import { ShoppingCart, Shield, UtensilsCrossed, Video as VideoIcon, Home as HomeIcon } from 'lucide-react';
import CampaignTeamLogin from '@/pages/CampaignTeamLogin';
import RevenueDashboard from '@/pages/RevenueDashboard';
import GroupingOverviewPage from '@/pages/GroupingOverviewPage';
import GoLivePage from '@/pages/GoLivePage';
import LiveStudioPage from '@/pages/LiveStudioPage';
import FinancialReportsPage from '@/pages/FinancialReportsPage';
import GivingIdentityRequestsPage from '@/pages/GivingIdentityRequestsPage';
import BankReconciliationPage from '@/pages/BankReconciliationPage';
import FundsPage from '@/pages/FundsPage';
import FinancialStatementsPage from '@/pages/FinancialStatementsPage';
import LedgerExplorerPage from '@/pages/LedgerExplorerPage';
import TeamHub from '@/pages/TeamHub';

// Components used as pages
import AdminRequestPage from '@/components/AdminRequestPage';
import SystemSettings from '@/components/SystemSettings';
import LandingPageEditor from '@/components/LandingPageEditor';
import ModuleLandingEditor from '@/pages/AdminModuleLandingsPage';
import StaffRolesManagement from '@/components/StaffRolesManagement';
import AdminChurchRoles from '@/pages/AdminChurchRoles';
import AdminOverseers from '@/pages/AdminOverseers';
import AdminMyProvince from '@/pages/AdminMyProvince';
import MemberGivingPage from '@/pages/MemberGivingPage';
import ProjectorView from '@/components/ProjectorView';
import AdminProfile from '@/components/AdminProfile';
import GitHubDashboard from '@/components/GitHubDashboard';
import QRHealth from '@/pages/QRHealth';
import VirtualStage from '@/components/VirtualStage';
import MealScanner from '@/components/MealScanner';
import StockScanner from '@/components/StockScanner';
import StandaloneSecurityDashboard from '@/components/StandaloneSecurityDashboard';
import StandaloneAccommodationDashboard from '@/components/StandaloneAccommodationDashboard';
import StandaloneDiningDashboard from '@/components/StandaloneDiningDashboard';
import StandalonePOS from '@/components/StandalonePOS';
import CCTVDashboard from '@/components/CCTVDashboard';
import SecurityCheckpoint from '@/components/SecurityCheckpoint';
import ChurchesManagement from '@/components/ChurchesManagement';
import CountryDirectoryPage from '@/pages/CountryDirectoryPage';
import AffiliatedDirectoryPage from '@/pages/AffiliatedDirectoryPage';
import AffiliateApplyPage from '@/pages/AffiliateApplyPage';
import AffiliateProfilePage from '@/pages/AffiliateProfilePage';
import AffiliatePortalPage from '@/pages/AffiliatePortalPage';
import VerifyAffiliationPage from '@/pages/VerifyAffiliationPage';
import AdminAffiliatedMinistries from '@/pages/AdminAffiliatedMinistries';
import { ChurchHierarchyExplorer } from '@/components/church/ChurchHierarchyExplorer';
import MemberGivingDashboard from '@/components/church/MemberGivingDashboard';
import MemberPortalPage from '@/pages/MemberPortalPage';
import DistrictLeaderboardPage from '@/pages/DistrictLeaderboardPage';
import PublicLeaderboardPage from '@/pages/PublicLeaderboardPage';
import MaintenanceCalendarPage from '@/pages/MaintenanceCalendarPage';
import EcosystemCalendarPage from '@/pages/EcosystemCalendarPage';
import HospitalAdminPage from '@/pages/HospitalAdminPage';
import {
  HospitalsDirectoryPage, HospitalStaffLoginPage, PublicHospitalPage,
} from '@/pages/HospitalPages';
import PublicChurchCalendarPage from '@/pages/PublicChurchCalendarPage';
import ChurchScreenPage from '@/pages/ChurchScreenPage';
import ChurchesMap from '@/components/ChurchesMap';
import ChurchModule from '@/pages/ChurchModule';
import ChurchRegionPage from '@/pages/ChurchRegionPage';
import WhatsAppAdminPage from '@/pages/WhatsAppAdminPage';
import SmsGatewaysPage from '@/pages/SmsGatewaysPage';
import FacilitiesModule from '@/pages/FacilitiesModule';
import InstitutionsManagement from '@/components/InstitutionsManagement';
import CampaignsManagement from '@/components/CampaignsManagement';
import CampaignPageEditor from '@/components/CampaignPageEditor';
import { CMSPagesList, CMSPageEditor } from '@/components/CMSPageBuilder';
import CommitteesManagement from '@/components/CommitteesManagement';
import MinistriesManagement from '@/components/MinistriesManagement';

// Delegate components
import DelegateDashboard from '@/components/delegate/DelegateDashboard';
// iter 629 follow-up — `/events/:slug/register` now redirects (the page it
// served fetched a route that does not exist), so the component is unrouted.
// The file stays in the tree pending a decision on the Form Builder's 22
// field types; nothing imports it.

// Dining / Staff
import KitchenStaffDashboard from '@/components/dining/KitchenStaffDashboard';
import POSStaffDashboard from '@/components/dining/POSStaffDashboard';
import HousingStaffDashboard from '@/components/accommodation/HousingStaffDashboard';


// ============== Wrapper Components ==============

const ProjectorViewPage = () => {
  const { stageId } = useParams();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  return <ProjectorView stageId={stageId} eventId={eventId} />;
};

const VirtualStagePage = () => {
  const { eventId, stageId } = useParams();
  return (
    <div className="min-h-screen bg-slate-100 p-4">
      <VirtualStage eventId={eventId} stageId={stageId} />
    </div>
  );
};

const MobileScannerWrapper = ({ type }) => {
  const { eventId } = useParams();
  if (type === 'meals') return <MealScanner eventId={eventId} />;
  if (type === 'stock') return <StockScanner eventId={eventId} />;
  return <div>Unknown scanner type</div>;
};

const SecurityCheckpointWrapper = () => {
  const { eventId } = useParams();
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Security Checkpoint</h1>
      <SecurityCheckpoint eventId={eventId} />
    </div>
  );
};

// ============== Auth Guards ==============

const RequireSecurityAuth = ({ children }) => {
  const token = localStorage.getItem('security_token') || localStorage.getItem('token');
  if (!token) return <Navigate to="/security/officer" replace />;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const isSecurityOfficer = payload.type === 'security_officer' || payload.role === 'security_officer';
    const isAdmin = payload.type === 'admin';
    if (!isSecurityOfficer && !isAdmin) {
      localStorage.removeItem('security_token');
      return <Navigate to="/security/officer" replace />;
    }
  } catch {
    localStorage.removeItem('security_token');
    return <Navigate to="/security/officer" replace />;
  }
  return children;
};

// ============== Legacy admin console redirector ==============
// Old admin consoles now redirect to `/admin/control-centre`.
// See routes/LegacyOrRedirect.jsx and routes/ChurchHierarchyLegacy.jsx.
// (Definitions moved out to keep this file under Babel's stack limit.)

// ============== Route Definitions ==============

// A URL nobody serves. A visitor goes to the public site; a signed-in person
// goes to their own workspace — landing them on the marketing page reads as
// "login logged me out" (iter 580).
const NotFoundRedirect = () => {
  const { userType, user } = useAuthStore();
  const location = useLocation();
  const signedIn = !!getToken();
  const target = signedIn ? homeFor(userType, user) : '/';
  // iter 624 — if their own home is a URL nobody serves, this redirect lands
  // back here and bounces forever. `security_officer` pointed at
  // `/security/gate`, which does not exist, and did exactly that. Fall back to
  // the shared dashboard rather than spin.
  if (target === location.pathname) {
    return <Navigate to={signedIn ? '/home' : '/'} replace />;
  }
  return <Navigate to={target} replace />;
};

// The old `/events/:slug/register` page. Anyone holding that link — including
// the Form Builder's "Open live form" — lands on the surface that works.
const RegisterRedirect = () => {
  const { eventId } = useParams();
  return <Navigate to={`/events/${eventId}`} replace />;
};


const AppRoutes = () => (
  <>
    <ImpersonationBanner />
    <AdminImpersonationBanner />
    <Routes>
    {/* ========== PUBLIC ========== */}
    {/*
      Subdomain takeover used to live here, but it was too aggressive — it
      caught preview job hostnames like `ai-native-events.preview.emergentagent.com`
      and tried to render an org_site for a non-existent slug, which 404'd
      and made the platform landing page look like it was failing to load.
      Custom-domain CMS serving will be reintroduced via a server-side ingress
      rule (handled by Emergent Support once DNS for abmhc.org / bishopmanjoro.org
      is mapped) — the platform root stays clean.
    */}
    <Route path="/" element={<OrganizationLandingPage />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/cookies" element={<CookiePolicy />} />
    <Route path="/s/:slug" element={<SchoolSlugRedirect />} />
    <Route path="/school/:tenantId/admissions/apply" element={<SchoolAdmissionsApply />} />
    <Route path="/qr-login" element={<QrLoginPage />} />
    <Route path="/landing-old" element={<Navigate to="/" replace />} />
    <Route path="/login" element={<UnifiedLoginPage />} />
    <Route path="/login-old" element={<Navigate to="/login" replace />} />
    <Route path="/admin/login" element={<Navigate to="/login?type=admin" replace />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/admin/reset-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ForgotPasswordPage />} />
    <Route path="/admin/register" element={<AdminRequestPage />} />
    <Route path="/leaderboard/:token" element={<PublicLeaderboardPage />} />
    <Route path="/hospital/login" element={<HospitalStaffLoginPage />} />
    <Route path="/hospital/:slug" element={<PublicHospitalPage />} />
    <Route path="/church/:slug/calendar" element={<PublicChurchCalendarPage />} />
    {/* The calendar on a screen at the back of the hall — public, no chrome */}
    <Route path="/church/:slug/screen" element={<ChurchScreenPage />} />
    <Route path="/verify" element={<VerifyCertificatePage />} />
    <Route path="/verify/:serial" element={<VerifyCertificatePage />} />
    <Route path="/certificates/:id/print" element={<PrintCertificatePage />} />
    <Route path="/event/:eventId" element={<EventLanding />} />
    <Route path="/events/:eventId" element={<EventLanding />} />
    <Route path="/event/:eventId/accessibility" element={<DelegateAccessibility />} />
    <Route path="/event/:eventId/accessibility/display" element={<AccessibilityDisplay />} />
    <Route path="/event/:eventId/accessibility/library" element={<CaptionsLibrary />} />
    <Route path="/event/:eventId/accessibility/source/:sessionId" element={<AccessibilitySource />} />
    <Route path="/invite/:token" element={<InvitationAccept />} />
    <Route path="/invitation/:token" element={<InvitationAccept />} />
    <Route path="/accept-invitation/:token" element={<InvitationAccept />} />
    <Route path="/payment/success" element={<PaymentSuccessPage />} />
    <Route path="/payment/cancel" element={<PaymentCancelPage />} />

    {/* ========== MODULE LANDING PAGES ========== */}
    <Route path="/events" element={<EventsLandingPage />} />
    <Route path="/find-church" element={<FindChurchPage />} />
    <Route path="/giving" element={<GivingLandingPage />} />
    <Route path="/bible-home" element={<BibleLandingPage />} />
    <Route path="/schools" element={<SchoolsDirectoryPage />} />
    <Route path="/media" element={<SermonsPublicPage />} />
    <Route path="/tv" element={<FaithWorksTVPage />} />
    <Route path="/faith-works-tv" element={<FaithWorksTVPage />} />
    <Route path="/media/:slug" element={<SermonPage />} />

    {/* ========== PROJECTOR / VIRTUAL STAGE ========== */}
    <Route path="/projector/:stageId" element={<ProjectorViewPage />} />
    <Route path="/event/:eventId/stage/:stageId" element={<VirtualStagePage />} />

    {/* ========== STAFF LOGIN (Public) — legacy routes superseded by /pos/login etc. below ========== */}

    {/* ========== MEMBER PORTAL ========== */}
    <Route path="/member/login" element={<MemberLoginPage />} />
    <Route path="/member/register" element={<MemberRegisterPage />} />
    <Route path="/member" element={<MemberPortal />} />
    <Route path="/member/giving" element={<MemberGivingPage />} />

    {/* ========== ALUMNI PORTAL ========== */}
    <Route path="/alumni-portal" element={<AlumniPortalPage />} />

    {/* ========== WALLET & PARENT SCAN (PUBLIC) ========== */}
    <Route path="/wallet/:serial" element={<WalletCardPage />} />
    <Route path="/parent-scan/:token" element={<ParentScanPage />} />
    <Route path="/ai-tutor" element={<AITutorPage />} />
    <Route path="/copilot" element={<CopilotPage />} />
    <Route path="/copilot/proposals/:proposalId" element={<CopilotPage />} />
    <Route path="/member/reset-password" element={<ForgotPasswordPage />} />

    {/* ========== PUBLIC ORG / CMS ========== */}
    <Route path="/org/:unitCode" element={<PublicOrgPage />} />
    <Route path="/org/:unitCode/blog" element={<PublicOrgPage />} />
    <Route path="/org/:unitCode/blog/:postSlug" element={<PublicOrgPage />} />
    <Route path="/org/:unitCode/events" element={<PublicOrgPage />} />
    <Route path="/org/:unitCode/media" element={<PublicOrgPage />} />
    <Route path="/page/:slug" element={<CMSPageView />} />

    {/* ========== CHURCHES ========== */}
    <Route path="/churches" element={<ChurchesDirectory />} />
    <Route path="/churches/bishop-manjoro" element={<BishopManjoroPage />} />
    <Route path="/churches/:slug" element={<ChurchPublicPage />} />
    <Route path="/churches/:slug/welcome" element={<ChurchWelcomePage />} />
    <Route path="/churches/:slug/live" element={<ChurchLivePage />} />
    <Route path="/churches/:slug/give" element={<ChurchGivingPage />} />

    {/* ========== CAMPAIGNS / DONATIONS ========== */}
    <Route path="/campaigns" element={<CampaignsList />} />
    <Route path="/campaigns/:slug" element={<CampaignLanding />} />
    <Route path="/campaigns/:slug/donate" element={<CampaignDonate />} />
    <Route path="/campaigns/:slug/embed" element={<CampaignTrackerEmbed />} />
    <Route path="/campaigns/:slug/milestones" element={<CampaignMilestones />} />
    <Route path="/campaigns/:slug/gallery" element={<CampaignGallery />} />
    <Route path="/campaigns/:slug/updates" element={<CampaignUpdates />} />
    <Route path="/campaigns/:slug/contact" element={<CampaignContact />} />
    <Route path="/my-campaigns" element={<MyCampaigns />} />
    <Route path="/campaigns/login" element={<CampaignTeamLogin />} />

    {/* ========== ORG-SITE CMS (ABMHC + future tenants) ========== */}
    <Route path="/abmhc" element={<OrgSitePage slug="abmhc" />} />
    <Route path="/bishop-manjoro" element={<OrgSitePage slug="bishop-manjoro" />} />
    <Route path="/sites/:slug" element={<OrgSitePage />} />
    <Route path="/admin/church-site" element={<AdminChurchSitePage />} />
    <Route path="/admin/abmhc/featured-media" element={<RequireAuth type="admin"><AdminABMHCFeaturedMedia /></RequireAuth>} />
    <Route path="/admin/abmhc/onecom" element={<RequireAuth type="admin"><AdminABMHCOneComCMS /></RequireAuth>} />
    <Route path="/admin/fhbf/onecom" element={<RequireAuth type="admin"><AdminFHBFOneComCMS /></RequireAuth>} />
    <Route path="/admin/landing" element={<RequireAuth type="admin" superOnly><AdminLandingEditor /></RequireAuth>} />
    <Route path="/admin/heroes" element={<RequireAuth type="admin" superOnly><AdminHeroImages /></RequireAuth>} />
    <Route path="/admin/tenant-domains" element={<RequireAuth type="admin"><AdminTenantDomains /></RequireAuth>} />
    <Route path="/admin/embeds" element={<RequireAuth type="admin"><AdminEmbedsHealth /></RequireAuth>} />
    <Route path="/admin/tributes" element={<AdminTributesPage />} />
    <Route path="/admin/tributes/mother-general" element={<RequireAuth type="admin"><MotherGeneralTributesAdmin /></RequireAuth>} />
    <Route path="/admin/presentations" element={<RequireAuth type="admin"><PresentationStudio /></RequireAuth>} />
    <Route path="/admin/projects" element={<RequireAuth type="admin"><ProjectWorkspace /></RequireAuth>} />
    <Route path="/admin/media-library" element={<RequireAuth type="admin"><AdminMediaLibraryPage /></RequireAuth>} />
    <Route path="/help/broadcasting" element={<BroadcastingGuidePage />} />
    <Route path="/admin/prayer-requests" element={<AdminPrayerRequestsPage />} />
    <Route path="/admin/appointments" element={<AdminAppointmentsPage />} />
    <Route path="/admin/patient-stories" element={<AdminPatientStoriesPage />} />
    <Route path="/admin/donation-pages" element={<AdminDonationPagesPage />} />
    <Route path="/admin/school-wiring" element={<AdminSchoolWiringPage />} />
    <Route path="/admin/school-wiring/:tenantId" element={<AdminSchoolWiringPage />} />
    <Route path="/give/:slug" element={<GivePage />} />
    <Route path="/studio" element={<StudioPortal />} />
    <Route path="/studio/login" element={<StudioLogin />} />
    <Route path="/studio/book" element={<StudioBookPublic />} />
    {/* Ops portals — POS / Security / Dining / CCTV / Housing */}
    <Route path="/pos/login" element={<TeamLoginPortal system="pos" title="Point of Sale" subtitle="Sign in to record sales" icon={ShoppingCart} accent="emerald" membershipCheckUrl="/ops/pos/team/me" defaultLanding="/pos" />} />
    <Route path="/security/login" element={<TeamLoginPortal system="security" title="Security" subtitle="Sign in to file incident reports" icon={Shield} accent="red" membershipCheckUrl="/ops/security/team/me" defaultLanding="/security" />} />
    <Route path="/dining/login" element={<TeamLoginPortal system="dining" title="Dining" subtitle="Sign in to manage menus" icon={UtensilsCrossed} accent="amber" membershipCheckUrl="/ops/dining/team/me" defaultLanding="/dining" />} />
    <Route path="/cctv/login" element={<TeamLoginPortal system="cctv" title="CCTV" subtitle="Sign in to monitor cameras" icon={VideoIcon} accent="indigo" membershipCheckUrl="/ops/cctv/team/me" defaultLanding="/cctv" />} />
    <Route path="/housing/login" element={<TeamLoginPortal system="housing" title="Housing" subtitle="Sign in to manage rooms" icon={HomeIcon} accent="teal" membershipCheckUrl="/ops/housing/team/me" defaultLanding="/housing" />} />
    <Route path="/pos" element={<RequireAuth type="admin"><OpsPortal /></RequireAuth>} />
    <Route path="/admin/pos/tabs" element={<RequireAuth type="admin"><AdminPosTabs /></RequireAuth>} />
    <Route path="/admin/pos/registers" element={<RequireAuth type="admin"><AdminPosRegisters /></RequireAuth>} />
    <Route path="/admin/wifi" element={<RequireAuth type="admin"><AdminWiFi /></RequireAuth>} />
    <Route path="/admin/lab" element={<RequireAuth type="admin"><AdminLabPCs /></RequireAuth>} />
    <Route path="/portal/wifi" element={<WiFiCaptivePortal />} />
    <Route path="/wifi/portal" element={<WiFiCaptivePortal />} />
    <Route path="/admin/obs" element={<RequireAuth type="admin"><AdminObsBroadcasts /></RequireAuth>} />
    <Route path="/admin/video-editor" element={<RequireAuth type="admin"><AdminVideoEditor /></RequireAuth>} />
    <Route path="/pos/display/:sessionId" element={<PosCustomerDisplay />} />
    <Route path="/security" element={<RequireAuth type="admin"><OpsPortal /></RequireAuth>} />
    <Route path="/dining" element={<RequireAuth type="admin"><OpsPortal /></RequireAuth>} />
    <Route path="/cctv" element={<RequireAuth type="admin"><OpsPortal /></RequireAuth>} />
    <Route path="/housing" element={<RequireAuth type="admin"><OpsPortal /></RequireAuth>} />
    <Route path="/housing/book" element={<HousingBookPublic />} />
    <Route path="/ops" element={<RequireAuth type="admin"><OpsHub /></RequireAuth>} />
    <Route path="/admin/finance/revenue" element={<RequireAuth type="admin" platformOnly><RevenueDashboard /></RequireAuth>} />
    <Route path="/admin/finance/reports" element={<RequireAuth type="admin"><FinancialReportsPage /></RequireAuth>} />
    <Route path="/admin/finance/ledger" element={<RequireAuth type="admin"><LedgerExplorerPage /></RequireAuth>} />
    <Route path="/admin/giving/identity-requests" element={<RequireAuth type="admin"><GivingIdentityRequestsPage /></RequireAuth>} />
    <Route path="/admin/finance/bank-reconciliation" element={<RequireAuth type="admin"><BankReconciliationPage /></RequireAuth>} />
    <Route path="/admin/finance/statements" element={<RequireAuth type="admin"><FinancialStatementsPage /></RequireAuth>} />
    <Route path="/admin/finance/funds" element={<RequireAuth type="admin"><FundsPage /></RequireAuth>} />
    <Route path="/admin/teams" element={<RequireAuth type="admin"><TeamHub /></RequireAuth>} />
    <Route path="/donate" element={<DonationPortalPage />} />
    <Route path="/donate-portal" element={<Navigate to="/campaigns/abmhc" replace />} />

    {/* ========== BIBLE ========== */}
    <Route path="/bible" element={<BibleHome />} />
    <Route path="/bible/:book" element={<BibleHome />} />
    <Route path="/bible/:book/:chapter" element={<BibleHome />} />
    <Route path="/bible-reader" element={<BiblePage />} />
    <Route path="/bible-reader/:book" element={<BiblePage />} />
    <Route path="/bible-reader/:book/:chapter" element={<BiblePage />} />
    <Route path="/bible/plans/:planId/read" element={<RequireAuth><BibleReadingPlanPage /></RequireAuth>} />

    {/* ========== EVENT REGISTRATION ========== */}
    {/* This page fetched `/events/by-slug/{slug}`, a route that does not exist,
        and swallowed the 404 — so the form never advanced. Its payload also
        omitted the required password. `/events/:eventId` (EventLanding) is the
        registration surface that works, so this address now goes there. */}
    <Route path="/events/:eventId/register" element={<RegisterRedirect />} />

    {/* ========== SCHOOL (Public) ========== */}
    <Route path="/school/:tenantId/quiz/:quizId/take" element={<TakeQuizPage />} />
    <Route path="/school/:tenantId/exam/:examId/take" element={<TakeExamPage />} />
    <Route path="/school/:tenantId/login" element={<SchoolLoginPage />} />
    <Route path="/school/:tenantId/reset-password" element={<SchoolResetPasswordPage />} />
    <Route path="/school/:tenantId/change-password" element={<SchoolChangePasswordPage />} />
    <Route path="/school/:tenantId/website" element={<SchoolWebsitePage />} />
    <Route path="/school/:tenantId/apply" element={<SchoolApplyPage />} />
    <Route path="/school/:tenantId/teacher" element={<RequireAuth><TeacherPortal /></RequireAuth>} />
    <Route path="/school/:tenantId/student" element={<RequireAuth><StudentPortal /></RequireAuth>} />
    <Route path="/school/:tenantId/parent" element={<RequireAuth><ParentPortal /></RequireAuth>} />
    <Route path="/school/:tenantId/live-classes" element={<RequireAuth><LiveClassroomList /></RequireAuth>} />
    <Route path="/school/:tenantId/live-classes/:sessionId" element={<RequireAuth><LiveClassroomRoom /></RequireAuth>} />

    {/* ========== MINISTRY SHOP (Public) ========== */}
    <Route path="/shop" element={<ShopPage />} />
    <Route path="/shop/product/:slug" element={<ShopProductPage />} />
    <Route path="/shop/checkout" element={<ShopCheckoutPage />} />
    <Route path="/shop/order/:orderNumber" element={<ShopOrderPage />} />
    <Route path="/admin/fwtv" element={<FaithWorksTVAdmin />} />
    {/* e-book reader for books already bought — the store itself is now /shop */}
    <Route path="/library" element={<ManjaroBooksPage />} />

    {/* ========== ADMIN — AUTH REQUIRED ========== */}
    {/* iter-438k Phase 3 — Adaptive Home Dashboard (any authenticated user) */}
    <Route path="/home" element={<RequireAuth><AdaptiveHomeDashboard /></RequireAuth>} />
    {/* iter-438k Phase 6 — Dynamic workspace page (server-driven sidebar) */}
    <Route path="/workspace/:key" element={<RequireAuth type="admin"><WorkspacePage /></RequireAuth>} />
    {/* Event-scoped admins are allowed past superOnly (RequireAuth) — the
        dashboard renders the event-only view for them. */}
    <Route path="/admin" element={<RequireAuth type="admin" superOnly><AdminDashboard /></RequireAuth>} />
    <Route path="/admin/event/:eventId" element={<RequireAuth type="admin"><AdminEventPage /></RequireAuth>} />
    <Route path="/admin/event/:eventId/preview" element={<RequireAuth type="admin"><DelegatePreview /></RequireAuth>} />
    <Route path="/admin/settings" element={<RequireAuth type="admin" superOnly><SystemSettings /></RequireAuth>} />
    <Route path="/admin/portal-settings" element={<Navigate to="/admin/settings" replace />} />
    <Route path="/admin/landing-editor" element={<RequireAuth type="admin" superOnly><AdminLandingEditor /></RequireAuth>} />
    <Route path="/admin/landing-editor/legacy" element={<RequireAuth type="admin" superOnly><LandingPageEditor /></RequireAuth>} />
    <Route path="/admin/module-landing-pages" element={<RequireAuth type="admin" superOnly><ModuleLandingEditor /></RequireAuth>} />
    <Route path="/admin/staff-roles" element={<RequireAuth type="admin" superOnly><StaffRolesManagement /></RequireAuth>} />
    <Route path="/admin/church-roles" element={<RequireAuth type="admin" superOnly><AdminChurchRoles /></RequireAuth>} />
    <Route path="/admin/overseers" element={<RequireAuth type="admin" superOnly><AdminOverseers /></RequireAuth>} />
    <Route path="/admin/my-province" element={<RequireAuth type="admin"><AdminMyProvince /></RequireAuth>} />
    <Route path="/admin/rbac/scopes" element={<RequireAuth type="admin" superOnly><AdminScopesPage /></RequireAuth>} />
    <Route path="/admin/whatsapp" element={<RequireAuth type="admin" superOnly><WhatsAppAdminPage /></RequireAuth>} />
    {/* Organisation-scoped: a church or school office runs its own gateway, so
        this is not superOnly — the server gates every call on `sms.*` plus the
        caller's organisation reach. */}
    <Route path="/admin/sms" element={<RequireAuth type="admin"><SmsGatewaysPage /></RequireAuth>} />
    <Route path="/church/:slug" element={<PublicChurchPage />} />
    <Route path="/admin/github" element={<RequireAuth type="admin" superOnly><GitHubDashboard /></RequireAuth>} />
    <Route path="/admin/qr-health" element={<RequireAuth type="admin" superOnly><QRHealth /></RequireAuth>} />
    <Route path="/admin/profile" element={<RequireAuth type="admin"><AdminProfile /></RequireAuth>} />
    <Route path="/admin/bible" element={<RequireAuth type="admin" superOnly><AdminBibleDashboard /></RequireAuth>} />
    <Route path="/admin/finance" element={<RequireAuth type="admin" superOnly><AdminFinanceDashboard /></RequireAuth>} />
    <Route path="/admin/ledger" element={<RequireAuth type="admin" superOnly><AdminUnifiedLedger /></RequireAuth>} />
    <Route path="/admin/storage" element={<RequireAuth type="admin" superOnly><AdminStoragePage /></RequireAuth>} />
    {/* Custodians only — the page itself refuses anyone not appointed. */}
    <Route path="/admin/finance/access" element={<RequireAuth type="admin"><AdminFinancialAccess /></RequireAuth>} />
    <Route path="/admin/housing" element={<RequireAuth type="admin" superOnly><AdminUnifiedHousing /></RequireAuth>} />
    <Route path="/admin/ecosystem-hr" element={<RequireAuth type="admin" superOnly><AdminEcosystemHR /></RequireAuth>} />
    <Route path="/admin/permissions" element={<RequireAuth type="admin" superOnly><AdminPermissionMatrix /></RequireAuth>} />
    <Route path="/admin/scope-registrations" element={<RequireAuth type="admin" superOnly><AdminScopeRegistrations /></RequireAuth>} />
    <Route path="/admin/universal-scanner" element={<RequireAuth type="admin" superOnly><UniversalScanner /></RequireAuth>} />
    <Route path="/admin/data-cleanup" element={<RequireAuth type="admin" superOnly><AdminDataCleanup /></RequireAuth>} />
    <Route path="/admin/bank" element={<RequireAuth type="admin"><BankDashboard /></RequireAuth>} />
    <Route path="/bank" element={<RequireAuth type="admin"><BankDashboard /></RequireAuth>} />
    <Route path="/my/wallet" element={<RequireAuth><MyWallet /></RequireAuth>} />
    <Route path="/my-wallet" element={<RequireAuth><MyWallet /></RequireAuth>} />
    <Route path="/admin/incidents" element={<RequireAuth type="admin"><IncidentsDashboard /></RequireAuth>} />
    <Route path="/admin/gate-pass" element={<RequireAuth type="admin"><GatePassPage /></RequireAuth>} />
    {/* Unified Control Centre — the single destination for RBAC + Hierarchy.
        Old admin URLs below redirect here. To reach a legacy screen during
        the transition, append ?legacy=1 (bypasses the Navigate below). */}
    <Route path="/admin/control-centre" element={<RequireAuth type="admin"><AdminControlCentre /></RequireAuth>} />
    <Route path="/admin/rbac-audit" element={<Navigate to="/admin/control-centre" replace />} />
    <Route path="/admin/role-builder" element={<Navigate to="/admin/control-centre" replace />} />
    {/* Governance — the standalone RBAC screens. Control Centre stays the hub;
        these are the deep tools it links out to (super-admin only). */}
    <Route path="/admin/governance/rbac-audit" element={<RequireAuth type="admin" superOnly><AdminRbacAudit /></RequireAuth>} />
    <Route path="/admin/governance/role-builder" element={<RequireAuth type="admin" superOnly><AdminRoleBuilder /></RequireAuth>} />
    <Route path="/admin/governance/team-permissions" element={<RequireAuth type="admin" superOnly><AdminTeamPermissions /></RequireAuth>} />
    <Route path="/admin/directory-sharing" element={<RequireAuth type="admin"><AdminDirectorySharing /></RequireAuth>} />
    <Route path="/admin/hierarchy-roles" element={<RequireAuth type="admin"><AdminHierarchyRoles /></RequireAuth>} />
    <Route path="/admin/duplicate-rolls" element={<RequireAuth type="admin"><AdminDuplicateRolls /></RequireAuth>} />
    <Route path="/admin/governance/denied-attempts" element={<RequireAuth type="admin" superOnly><AdminDeniedAttempts /></RequireAuth>} />
    <Route path="/admin/cross-school-analytics" element={<RequireAuth type="admin" superOnly><AdminCrossSchoolAnalytics /></RequireAuth>} />
    <Route path="/admin/pricing-plans" element={<RequireAuth type="admin" superOnly><AdminPricingPlans /></RequireAuth>} />
    <Route path="/admin/bible-plans" element={<RequireAuth type="admin" superOnly><AdminBiblePlans /></RequireAuth>} />
    <Route path="/admin/qr-badges" element={<RequireAuth type="admin"><AdminQrBadges /></RequireAuth>} />
    <Route path="/admin/crm" element={<RequireAuth type="admin" superOnly><AdminCRM /></RequireAuth>} />
    {/* The shared person directory: search, profile, pledging and duplicate review */}
    <Route path="/admin/people" element={<RequireAuth type="admin"><AdminPeopleDirectory /></RequireAuth>} />
    <Route path="/admin/accommodation-ops" element={<RequireAuth type="admin"><AdminAccommodationOps /></RequireAuth>} />
    <Route path="/accommodation/apply" element={<AccommodationApply />} />
    <Route path="/admin/crm/contacts/:id" element={<RequireAuth type="admin" superOnly><AdminCRMContactDetail /></RequireAuth>} />
    <Route path="/admin/backup" element={<RequireAuth type="admin" superOnly><AdminBackupCentre /></RequireAuth>} />
    <Route path="/admin/documents/audit" element={<RequireAuth type="admin" superOnly><AdminDocumentAudit /></RequireAuth>} />
    <Route path="/admin/persons" element={<RequireAuth type="admin" superOnly><AdminPersons /></RequireAuth>} />
    <Route path="/admin/persons/:id" element={<RequireAuth type="admin" superOnly><AdminPersonProfile /></RequireAuth>} />
    <Route path="/admin/church-members/:memberId" element={<RequireAuth type="admin" superOnly><AdminChurchMember360 /></RequireAuth>} />
    <Route path="/admin/churches/:churchId/home-group-split" element={<RequireAuth type="admin" superOnly><AdminChurchHomeGroupSplit /></RequireAuth>} />
    <Route path="/admin/leadership" element={<RequireAuth type="admin" superOnly><AdminLeadership /></RequireAuth>} />
    <Route path="/admin/church-ops" element={<RequireAuth type="admin" superOnly><AdminChurchOpsHub /></RequireAuth>} />
    <Route path="/admin/team-permissions" element={<Navigate to="/admin/control-centre" replace />} />
    <Route path="/admin/worship" element={<RequireAuth type="admin"><AdminWorshipHub /></RequireAuth>} />
    <Route path="/worship/present/:serviceId" element={<RequireAuth type="admin"><PresenterConsole /></RequireAuth>} />
    <Route path="/worship/output/:serviceId/:mode" element={<RequireAuth type="admin"><PresenterOutput /></RequireAuth>} />
    <Route path="/portal" element={<PublicSongPortal />} />
    <Route path="/leadership" element={<PublicLeadershipPage />} />
    <Route path="/apply/:tenantId" element={<ApplyPage />} />
    <Route path="/reference/:token" element={<ReferencePage />} />
    <Route path="/leadership/:slug" element={<PublicLeadershipPage />} />
    <Route path="/portal/service/:serviceId" element={<PublicSongPortal />} />
    <Route path="/portal/:slug/:date" element={<PublicSongPortal />} />
    <Route path="/church/:churchId/join" element={<ChurchJoinPage />} />
    <Route path="/admin/system-events" element={<RequireAuth type="admin" superOnly><AdminSystemEvents /></RequireAuth>} />
    <Route path="/admin/notifications/preferences/:id" element={<RequireAuth type="admin" superOnly><AdminNotificationPreferences /></RequireAuth>} />
    <Route path="/admin/executive" element={<RequireAuth type="admin" superOnly><AdminExecutive /></RequireAuth>} />
    <Route path="/admin/audit/duplication" element={<RequireAuth type="admin" superOnly><AdminDuplicationAudit /></RequireAuth>} />
    <Route path="/admin/security/console" element={<RequireAuth type="admin" superOnly><AdminSecurityConsole /></RequireAuth>} />
    <Route path="/admin/security/operations" element={<RequireAuth type="admin"><SecurityOps /></RequireAuth>} />
    <Route path="/admin/audit/church" element={<RequireAuth type="admin" superOnly><AdminChurchAudit /></RequireAuth>} />
    <Route path="/admin/badge-designer" element={<RequireAuth type="admin"><AdminBadgeDesigner /></RequireAuth>} />
    <Route path="/admin/event/:eventId/checkin-universal" element={<RequireAuth type="admin"><EventCheckInUniversal /></RequireAuth>} />
    <Route path="/admin/shop" element={<RequireAuth type="admin"><AdminShop /></RequireAuth>} />
    <Route path="/admin/bookshop" element={<RequireAuth type="admin"><BookshopAdmin /></RequireAuth>} />
    <Route path="/admin/bookshop/team" element={<RequireAuth type="admin"><BookshopTeamPage /></RequireAuth>} />
    <Route path="/admin/fwtv/premium" element={<RequireAuth type="admin"><FwtvPremiumAdmin /></RequireAuth>} />
    <Route path="/admin/fwtv/studio" element={<RequireAuth type="admin"><FwtvStudioAdmin /></RequireAuth>} />
    <Route path="/admin/fwtv/team" element={<RequireAuth type="admin"><FwtvTeamPage /></RequireAuth>} />
    <Route path="/admin/media-intel" element={<RequireAuth type="admin"><AdminMediaIntel /></RequireAuth>} />
    <Route path="/admin/housing/v2" element={<RequireAuth type="admin"><AdminHousingV2 /></RequireAuth>} />
    <Route path="/admin/qr-scanner" element={<RequireAuth type="admin"><AdminUniversalScanner /></RequireAuth>} />
    <Route path="/admin/qr-labels" element={<RequireAuth type="admin"><AdminQrLabels /></RequireAuth>} />
    <Route path="/admin/donor-timeline" element={<RequireAuth type="admin" superOnly><DonorTimeline /></RequireAuth>} />
    <Route path="/register" element={<PublicScopeRegister />} />
    <Route path="/me/employments" element={<MyEmployments />} />

    {/* Admin — CMS */}
    <Route path="/admin/cms" element={<RequireAuth type="admin" superOnly><CMSPagesList /></RequireAuth>} />
    <Route path="/admin/cms/pages/:pageId" element={<RequireAuth type="admin" superOnly><CMSPageEditor /></RequireAuth>} />

    {/* Admin — Churches & Institutions */}
    <Route path="/admin/churches" element={<RequireAuth type="admin"><ChurchesManagement /></RequireAuth>} />
    <Route path="/admin/churches/country-directory" element={<RequireAuth type="admin" superOnly><CountryDirectoryPage /></RequireAuth>} />

    {/* Affiliated Churches & Ministries — separate from the FWM network */}
    <Route path="/affiliated-ministries" element={<AffiliatedDirectoryPage />} />
    <Route path="/affiliated-ministries/apply" element={<AffiliateApplyPage />} />
    <Route path="/affiliated-ministries/:slug" element={<AffiliateProfilePage />} />
    <Route path="/affiliate-portal/:token" element={<AffiliatePortalPage />} />
    <Route path="/verify-affiliation/:serial" element={<VerifyAffiliationPage />} />
    <Route path="/admin/affiliates" element={<RequireAuth type="admin" superOnly><AdminAffiliatedMinistries /></RequireAuth>} />
    <Route path="/admin/organisations/:orgId/leaderboard" element={<RequireAuth type="admin"><DistrictLeaderboardPage /></RequireAuth>} />
    <Route path="/admin/maintenance/calendar" element={<RequireAuth type="admin"><MaintenanceCalendarPage /></RequireAuth>} />
    <Route path="/admin/calendar" element={<RequireAuth type="admin"><EcosystemCalendarPage /></RequireAuth>} />
    <Route path="/admin/hospitals" element={<RequireAuth type="admin" superOnly><HospitalsDirectoryPage /></RequireAuth>} />
    <Route path="/admin/hospital/:hospitalId" element={<HospitalAdminPage />} />
    <Route path="/admin/churches/hierarchy" element={<Navigate to="/admin/control-centre" replace />} />
    <Route path="/admin/churches/region/:churchId" element={<RequireAuth type="admin"><ChurchRegionPage /></RequireAuth>} />
    <Route path="/admin/members/:memberId/giving" element={<RequireAuth type="admin"><div className="min-h-screen bg-slate-50"><MemberGivingDashboard /></div></RequireAuth>} />
    <Route path="/member-portal" element={<MemberPortalPage />} />
    <Route path="/admin/churches/map" element={<RequireAuth type="admin" superOnly><ChurchesMap /></RequireAuth>} />
    <Route path="/grouping/:shareToken" element={<GroupingOverviewPage />} />
    <Route path="/admin/churches/:churchId/go-live" element={<RequireAuth type="admin"><GoLivePage /></RequireAuth>} />
    <Route path="/admin/live/:scope/:ident" element={<RequireAuth type="admin"><LiveStudioPage /></RequireAuth>} />
    <Route path="/admin/churches/:churchId/overview" element={<RequireAuth type="admin"><GroupingOverviewPage /></RequireAuth>} />
    <Route path="/admin/churches/:churchId" element={<RequireAuth type="admin"><ChurchModule /></RequireAuth>} />
    <Route path="/admin/facilities" element={<RequireAuth type="admin"><FacilitiesModule /></RequireAuth>} />
    <Route path="/admin/institutions" element={<RequireAuth type="admin" superOnly><InstitutionsManagement /></RequireAuth>} />

    {/* Admin — Campaigns & Donations */}
    <Route path="/admin/campaigns" element={<RequireAuth type="admin" superOnly><CampaignsManagement /></RequireAuth>} />
    <Route path="/admin/campaigns/:campaignId/edit" element={<RequireAuth type="admin" superOnly><CampaignPageEditor /></RequireAuth>} />
    <Route path="/admin/donations" element={<RequireAuth type="admin" superOnly><AdminDonationsPage /></RequireAuth>} />
    <Route path="/admin/giving" element={<RequireAuth type="admin"><div className="min-h-screen bg-slate-50"><GivingDashboard /></div></RequireAuth>} />
    <Route path="/admin/approvals" element={<RequireAuth type="admin"><ApprovalInbox /></RequireAuth>} />
    <Route path="/admin/role-changes" element={<RequireAuth type="admin" superOnly><RoleChangeLog /></RequireAuth>} />

    {/* Admin — Books */}

    {/* Admin — Committees & Ministries */}
    <Route path="/admin/committees" element={<RequireAuth type="admin" superOnly><CommitteesManagement /></RequireAuth>} />
    <Route path="/admin/ministries" element={<RequireAuth type="admin" superOnly><MinistriesManagement /></RequireAuth>} />

    {/* Admin — Schools */}
    <Route path="/admin/schools" element={<RequireAuth type="admin" superOnly><SchoolManagement /></RequireAuth>} />
    <Route path="/admin/schools/:tenantId" element={<RequireAuth type="admin"><SchoolDetailPage /></RequireAuth>} />

    {/* ========== SUPER ADMIN / PLATFORM ========== */}
    <Route path="/platform-apply" element={<PlatformApplyPage />} />
    <Route path="/pricing" element={<PricingPage />} />
    <Route path="/admin/home" element={<RequireAuth type="admin"><AdminHome /></RequireAuth>} />
    {/* Platform portal — Tenants · Plans · Applications · Billing · Platform Settings */}
    <Route path="/admin/platform" element={<RequireAuth type="admin" superOnly><SuperAdminDashboard /></RequireAuth>} />
    <Route path="/superadmin" element={<Navigate to="/admin/control-centre" replace />} />
    <Route path="/admin/transcription" element={<RequireAuth><TranscriptionJobsAdmin /></RequireAuth>} />

    {/* ========== SCANNING / KIOSK STATIONS ========== */}
    <Route path="/admin/checkin/:eventId" element={<RequireAuth type="admin"><CheckinStation /></RequireAuth>} />
    <Route path="/admin/meal-scan/:eventId" element={<RequireAuth type="admin"><MealScanStation /></RequireAuth>} />
    <Route path="/admin/accommodation-scan/:eventId" element={<RequireAuth type="admin"><AccommodationScanStation /></RequireAuth>} />
    <Route path="/admin/kiosk/:eventId" element={<RequireAuth type="admin"><UnifiedKiosk /></RequireAuth>} />
    <Route path="/arrival/kiosk" element={<RequireAuth type="admin"><ArrivalKiosk /></RequireAuth>} />
    <Route path="/admin/security/:eventId" element={<RequireAuth type="admin"><DashboardLayout><SecurityCheckpointWrapper /></DashboardLayout></RequireAuth>} />
    <Route path="/scan/:eventId" element={<RequireAuth type="admin"><MobileScanner /></RequireAuth>} />
    <Route path="/admin/scan/meals/:eventId" element={<RequireAuth type="admin"><MobileScannerWrapper type="meals" /></RequireAuth>} />
    <Route path="/admin/scan/stock/:eventId" element={<RequireAuth type="admin"><MobileScannerWrapper type="stock" /></RequireAuth>} />

    {/* ========== STANDALONE DASHBOARDS ========== */}
    <Route path="/admin/security" element={<RequireAuth type="admin"><StandaloneSecurityDashboard /></RequireAuth>} />
    <Route path="/admin/accommodation" element={<RequireAuth type="admin"><StandaloneAccommodationDashboard /></RequireAuth>} />
    <Route path="/admin/dining" element={<RequireAuth type="admin"><StandaloneDiningDashboard /></RequireAuth>} />
    <Route path="/admin/pos" element={<RequireAuth type="admin"><StandalonePOS /></RequireAuth>} />
    <Route path="/admin/cctv" element={<RequireAuth type="admin"><CCTVDashboard /></RequireAuth>} />

    {/* ========== STAFF PORTALS ========== */}
    <Route path="/kitchen" element={<RequireAuth type="admin"><KitchenStaffDashboard /></RequireAuth>} />
    {/* Dedicated housing / dining staff portals. The shared ops logins at
        /housing/login and /dining/login are untouched — these are the
        module-specific sign-ins with their own dashboards. */}
    <Route path="/housing/staff-login" element={<HousingStaffLogin />} />
    <Route path="/housing/staff" element={<RequireAuth><HousingStaffDashboard /></RequireAuth>} />
    <Route path="/dining/staff-login" element={<DiningStaffLogin />} />
    <Route path="/dining/pos" element={<RequireAuth><POSStaffDashboard /></RequireAuth>} />
    {/* ========== POS / HOUSING superseded by /pos and /housing routes above (OpsPortal) ========== */}
    {/* ========== SECURITY OFFICER (legacy standalone dashboard preserved) ========== */}
    <Route path="/security/officer" element={<SecurityLoginPage />} />
    <Route path="/security/dashboard" element={<RequireSecurityAuth><StandaloneSecurityDashboard /></RequireSecurityAuth>} />

    {/* ========== DELEGATE ========== */}
    <Route path="/delegate" element={<RequireAuth type="delegate"><DelegatePortal /></RequireAuth>} />
    <Route path="/delegate/dashboard" element={<RequireAuth type="delegate"><DelegateDashboard /></RequireAuth>} />

    {/* ========== CATCH ALL ========== */}
    <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
  </>
);

export default AppRoutes;
